export type ReservedCheckoutWindow = Pick<Window, "closed" | "close" | "focus"> & {
  location: Pick<Location, "replace">;
};

/**
 * Browsers generally allow a new tab only during the original click event. Keep
 * an empty tab reserved while the server creates the Stripe Checkout Session,
 * then navigate that tab after the mutation succeeds.
 */
export function reserveCheckoutWindow(
  openWindow: () => Window | null = () => window.open("about:blank", "_blank"),
): ReservedCheckoutWindow | null {
  const checkoutWindow = openWindow();
  if (!checkoutWindow) return null;

  try {
    checkoutWindow.opener = null;
  } catch {
    // Some browsers expose a read-only opener. The cross-origin Stripe
    // navigation still replaces the reserved document below.
  }

  return checkoutWindow;
}

export function closeReservedCheckoutWindow(checkoutWindow: ReservedCheckoutWindow | null) {
  if (!checkoutWindow || checkoutWindow.closed) return;
  try {
    checkoutWindow.close();
  } catch {
    // A browser may revoke access after a cross-origin navigation.
  }
}

/**
 * Prefer the tab reserved during the click. If the browser blocked it or later
 * revoked access, continue in the current tab so Checkout is never stranded.
 */
export function handoffToCheckout(
  checkoutUrl: string,
  checkoutWindow: ReservedCheckoutWindow | null,
  navigateCurrentWindow: (url: string) => void = url => window.location.assign(url),
): "reserved_window" | "current_window" {
  if (checkoutWindow && !checkoutWindow.closed) {
    try {
      checkoutWindow.location.replace(checkoutUrl);
      try {
        checkoutWindow.focus();
      } catch {
        // Focus is best-effort; the checkout tab is already navigating.
      }
      return "reserved_window";
    } catch {
      closeReservedCheckoutWindow(checkoutWindow);
    }
  }

  navigateCurrentWindow(checkoutUrl);
  return "current_window";
}
